import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useGeoData, GeoCountry } from "@/hooks/useGeoData";
import { getPhoneCode } from "@/lib/countryPhoneCodes";
import { parsePhoneNumber } from "libphonenumber-js";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { OTPVerification } from "./OTPVerification";
import { PhoneInput } from "./PhoneInput";

interface PhoneSignUpProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const PhoneSignUp = ({ onBack, onSuccess }: PhoneSignUpProps) => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<GeoCountry | null>(null);
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOTP, setShowOTP] = useState(false);

  const handleCountryChange = (country: GeoCountry, code: string) => {
    setSelectedCountry(country);
    setCountryCode(code);
    // Clear any existing error when country changes
    setError(null);
  };

  // Note: Phone numbers are stored in Supabase Auth (auth.users), not in profiles table
  // Duplicate checking is handled by Supabase Auth when attempting to sign up

  const validatePhoneNumber = (phone: string, countryIso2: string): { valid: boolean; error?: string; e164?: string } => {
    const digits = phone.replace(/\D/g, "");
    
    if (!digits || digits.length === 0) {
      return { valid: false, error: t("entry.pleaseEnterPhoneNumber") };
    }
    
    // Ensure we have a valid country code
    if (!countryCode || !countryCode.startsWith("+")) {
      console.error("Invalid country code:", countryCode);
      return { valid: false, error: t("entry.pleaseSelectValidCountry") };
    }
    
    try {
      // First, try parsing with just the digits and country ISO2
      let phoneNumber = parsePhoneNumber(digits, countryIso2.toUpperCase() as any);
      
      // If that fails, try with the full number including country code
      if (!phoneNumber || !phoneNumber.isValid()) {
        const fullNumber = `${countryCode.replace(/\s/g, "")}${digits}`;
        console.log("Trying full number format:", fullNumber);
        phoneNumber = parsePhoneNumber(fullNumber);
      }
      
      if (!phoneNumber) {
        return { valid: false, error: t("entry.pleaseEnterValidPhoneNumber") };
      }
      
      if (!phoneNumber.isValid()) {
        return { valid: false, error: t("entry.pleaseEnterValidPhoneForCountry") };
      }
      
      // Return E.164 format (e.g., +1234567890)
      const e164 = phoneNumber.format("E.164");
      
      // Validate E.164 format
      if (!e164 || !e164.startsWith("+") || e164.length < 8) {
        console.error("Invalid E.164 format:", e164);
        return { valid: false, error: "Invalid phone number format" };
      }
      
      console.log("Validated phone number:", { 
        input: digits, 
        countryIso2: countryIso2, 
        countryCode,
        e164,
        isValid: phoneNumber.isValid(),
        detectedCountry: phoneNumber.country,
      });
      
      return { valid: true, e164 };
    } catch (error: any) {
      console.error("Phone validation error:", error);
      return { valid: false, error: error.message || t("entry.pleaseEnterValidPhoneNumber") };
    }
  };

  const sendOTP = async (): Promise<void> => {
    if (!selectedCountry) {
      setError(t("entry.pleaseSelectCountry"));
      return;
    }

    if (!phoneNumber || phoneNumber.trim().length === 0) {
      setError(t("entry.pleaseEnterPhoneNumber"));
      return;
    }

    // Validate phone number and get E.164 format
    const validation = validatePhoneNumber(phoneNumber, selectedCountry.iso2);
    if (!validation.valid || !validation.e164) {
      setError(validation.error || t("entry.pleaseEnterValidPhoneNumber"));
      return;
    }

    // Use validated E.164 format
    const fullPhone = validation.e164;
    console.log("Preparing to send OTP:", {
      rawInput: phoneNumber,
      country: selectedCountry.iso2,
      countryCode,
      formatted: fullPhone,
    });
    
    setIsLoading(true);
    setIsCheckingDuplicate(true);
    setError(null);

    try {
      // Send OTP via Supabase (creates user if doesn't exist)
      // Supabase Auth will handle duplicate checking
      console.log("Sending OTP to phone:", fullPhone);
      console.log("Request details:", {
        phone: fullPhone,
        shouldCreateUser: true,
      });
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout - OTP send took too long")), 30000); // 30 second timeout
      });
      
      const otpPromise = supabase.auth.signInWithOtp({
        phone: fullPhone,
        options: {
          shouldCreateUser: true,
        },
      });
      
      const { data, error: otpError } = await Promise.race([otpPromise, timeoutPromise]) as any;

      if (otpError) {
        console.error("OTP Error details:", {
          message: otpError.message,
          status: otpError.status,
          name: otpError.name,
          error: otpError,
        });
        
        // Handle 400 Bad Request errors specifically
        if (otpError.status === 400) {
          // Check for specific 400 error messages
          const errorMsg = otpError.message.toLowerCase();
          
          // Log the full error for debugging
          console.error("Supabase OTP 400 Error:", {
            message: otpError.message,
            fullError: otpError,
          });
          
          if (errorMsg.includes("invalid phone") || errorMsg.includes("phone number")) {
            setError(t("entry.pleaseEnterValidPhoneNumber"));
          } else if (errorMsg.includes("rate limit") || errorMsg.includes("too many")) {
            setError(t("entry.rateLimitExceeded"));
          } else if (
            errorMsg.includes("provider") || 
            errorMsg.includes("sms") ||
            errorMsg.includes("unsupported") ||
            errorMsg.includes("not configured") ||
            errorMsg.includes("not enabled")
          ) {
            setError(t("entry.smsUnavailable"));
          } else {
            // Show the actual error message for 400 errors (for debugging)
            console.error("Unhandled 400 error:", otpError.message);
            setError(t("entry.smsUnavailable")); // Default to SMS unavailable for unknown 400 errors
          }
          setIsLoading(false);
          setIsCheckingDuplicate(false);
          return;
        }
        
        // Handle specific error cases
        if (otpError.message.includes("already registered") || otpError.message.includes("already exists")) {
          setError(t("entry.alreadyRegistered"));
          setIsLoading(false);
          setIsCheckingDuplicate(false);
          return;
        }
        
        // Handle "Unsupported phone provider" error - be more specific
        if (
          otpError.message.toLowerCase().includes("unsupported phone provider") ||
          otpError.message.toLowerCase().includes("sms provider is not configured") ||
          otpError.message.toLowerCase().includes("sms provider not enabled")
        ) {
          setError(t("entry.unsupportedPhoneProvider"));
          setIsLoading(false);
          setIsCheckingDuplicate(false);
          return;
        }
        
        // Handle 500 Internal Server Error (database errors)
        if (otpError.status === 500) {
          console.error("Supabase OTP 500 Error (Database Error):", {
            message: otpError.message,
            fullError: otpError,
          });
          
          const errorMsg = otpError.message.toLowerCase();
          if (
            errorMsg.includes("database") ||
            errorMsg.includes("profile") ||
            errorMsg.includes("trigger") ||
            errorMsg.includes("constraint")
          ) {
            setError(t("entry.databaseErrorSavingUser") || "Database error saving new user. Please try again.");
          } else {
            setError(t("entry.serverError") || "Server error. Please try again later.");
          }
          setIsLoading(false);
          setIsCheckingDuplicate(false);
          return;
        }
        
        // Show the actual error message for debugging
        setError(otpError.message || t("entry.failedToSendCode"));
        setIsLoading(false);
        setIsCheckingDuplicate(false);
        return;
      }

      // Success - OTP was sent
      console.log("OTP sent successfully:", data);
      console.log("Response data:", JSON.stringify(data, null, 2));
      
      // Check if we actually got a success response
      if (data) {
        // Note: Supabase returns success when OTP is queued, not necessarily delivered
        // Check Supabase Dashboard → Logs → Auth Logs for actual delivery status
        console.log("✅ OTP request accepted by Supabase");
        console.log("📱 SMS should be sent via configured provider (Twilio, etc.)");
        console.log("");
        console.log("🔍 IMMEDIATE TROUBLESHOOTING STEPS:");
        console.log("1. Check Supabase Dashboard → Logs → Auth Logs");
        console.log("   Look for the OTP send attempt and any errors");
        console.log("");
        console.log("2. Check Twilio Console → Monitor → Logs → Messaging");
        console.log("   URL: https://console.twilio.com/us1/monitor/logs/messaging");
        console.log("   Look for SMS send attempts to", fullPhone);
        console.log("");
        console.log("3. Verify Twilio Trial Account Restrictions:");
        console.log("   - Trial accounts can ONLY send to verified phone numbers");
        console.log("   - Go to: https://console.twilio.com/us1/develop/phone-numbers/manage/verified");
        console.log("   - Add your phone number", fullPhone, "as a verified number");
        console.log("");
        console.log("4. Check Twilio Account Balance:");
        console.log("   - Go to: https://console.twilio.com/us1/develop/billing/overview");
        console.log("   - Ensure you have credits/balance");
        console.log("");
        console.log("5. Verify Twilio Message Service SID is correct");
        console.log("   - Check in Supabase: Authentication → Providers → Phone");
        console.log("   - Verify Message Service SID matches Twilio Console");
        
        setShowOTP(true);
        toast.warning(t("otp.codeSentCheckTwilio") || "Code sent. If not received, check Twilio logs.", {
          description: t("otp.troubleshootSms") || "⚠️ Most common issue: Twilio trial accounts can ONLY send to verified numbers. See console for steps.",
          duration: 10000,
          action: {
            label: "Use Email Instead",
            onClick: () => {
              // This will be handled by parent component
              window.location.reload();
            }
          }
        });
      } else {
        // No error but also no data - might be a silent failure
        console.warn("OTP request completed but no data returned");
        setError(t("entry.failedToSendCode") + " Please try again.");
        setIsLoading(false);
        setIsCheckingDuplicate(false);
      }
    } catch (err: any) {
      console.error("Unexpected error sending OTP:", err);
      console.error("Error type:", typeof err);
      console.error("Error details:", {
        message: err?.message,
        name: err?.name,
        stack: err?.stack,
        fullError: err,
      });
      
      // Handle timeout specifically
      if (err?.message?.includes("timeout")) {
        setError(t("entry.requestTimeout") || "Request timed out. Please check your connection and try again.");
      } else {
        setError(err?.message || t("entry.failedToSendCode"));
      }
    } finally {
      setIsLoading(false);
      setIsCheckingDuplicate(false);
    }
  };

  const verifyOTP = async (otp: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!selectedCountry) {
        return { success: false, error: "Country selection is required" };
      }
      
      // Re-validate and get E.164 format for verification
      const validation = validatePhoneNumber(phoneNumber, selectedCountry.iso2);
      if (!validation.valid || !validation.e164) {
        return { success: false, error: "Invalid phone number format" };
      }
      
      console.log("Verifying OTP:", {
        phone: validation.e164,
        tokenLength: otp.length,
        type: "sms",
      });
      
      const { data, error } = await supabase.auth.verifyOtp({
        phone: validation.e164,
        token: otp,
        type: "sms",
      });

      if (error) {
        console.error("OTP Verification Error:", {
          message: error.message,
          status: error.status,
          name: error.name,
          fullError: error,
        });
        
        // Handle 403 Forbidden specifically
        if (error.status === 403) {
          const errorMsg = error.message.toLowerCase();
          if (errorMsg.includes("expired") || errorMsg.includes("expire")) {
            return { success: false, error: t("otp.codeExpired") || "Verification code has expired. Please request a new one." };
          } else if (errorMsg.includes("already used") || errorMsg.includes("used")) {
            return { success: false, error: t("otp.codeAlreadyUsed") || "This code has already been used. Please request a new one." };
          } else if (errorMsg.includes("rate limit") || errorMsg.includes("too many")) {
            return { success: false, error: t("entry.rateLimitExceeded") };
          } else {
            return { success: false, error: t("otp.verificationForbidden") || "Verification failed. Please request a new code." };
          }
        }
        
        return { success: false, error: error.message || t("otp.invalidCode") || "Invalid verification code" };
      }
      
      console.log("OTP Verification Success:", {
        hasUser: !!data?.user,
        userId: data?.user?.id,
      });

      if (data.user) {
        // Check if profile exists, if not create it
        const { data: existingProfile } = await supabase
          .from("profiles")
          .select("id")
          .eq("id", data.user.id)
          .maybeSingle();

        // Use validated E.164 format
        const verifiedPhone = validation.e164;

        if (!existingProfile) {
          // Create profile if it doesn't exist
          await supabase.from("profiles").insert({
            id: data.user.id,
            name: verifiedPhone, // Temporary name, user will update in onboarding
          });
        }
        // Note: Phone number is stored in auth.users, not in profiles table

        return { success: true };
      }

      return { success: false, error: t("entry.verificationFailed") };
    } catch (err: any) {
      return { success: false, error: err.message || t("entry.verificationFailed") };
    }
  };

  const resendOTP = async (): Promise<void> => {
    await sendOTP();
  };

  if (showOTP) {
    // Use validated E.164 format for display (we already validated before showing OTP)
    const validation = selectedCountry 
      ? validatePhoneNumber(phoneNumber, selectedCountry.iso2)
      : null;
    const displayPhone = validation?.e164 || `${countryCode}${phoneNumber}`;
    
    return (
      <OTPVerification
        type="phone"
        identifier={displayPhone}
        onVerify={verifyOTP}
        onResend={resendOTP}
        onBack={() => setShowOTP(false)}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t("entry.back")}</span>
      </button>

      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">{t("entry.signUpWithPhoneTitle")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("entry.enterPhoneToGetStarted")}
        </p>
      </div>

      {/* Unified Phone Input */}
      <PhoneInput
        value={phoneNumber}
        onChange={setPhoneNumber}
        onCountryChange={handleCountryChange}
        selectedCountry={selectedCountry}
        countryCode={countryCode}
        error={error}
        disabled={isLoading}
        placeholder={t("entry.enterPhoneNumber")}
        autoFocus
      />

      {/* Send Verification Code Button */}
      <button
        onClick={sendOTP}
        disabled={isLoading || !phoneNumber || phoneNumber.trim().length === 0 || !selectedCountry}
        className={cn(
          "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
          "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
          "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
          "hover:scale-[1.02] active:scale-[0.98] transition-all",
          "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        )}
      >
        {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
        {isCheckingDuplicate
          ? t("entry.checking")
          : isLoading
          ? t("entry.sending")
          : t("entry.sendVerificationCode")}
      </button>
    </div>
  );
};

